"use client";

import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useForm, SubmitHandler } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  createTicketRequest,
  resetFormState,
} from "@/store/slices/ticket.slice";
import { Breadcrumb } from "@/components/common/Breadcrumb";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  StandardFormInput,
  StandardFormSelect,
  StandardFormTagInput,
} from "@/components/common/StandardFormInput";
import FormFieldWrapper from "@/components/coupon/formFieldWrapper";
import { Send, Ticket as TicketIcon } from "lucide-react";
import {
  CreateTicketDto,
  TicketPriority,
  TicketType,
} from "@/types/models/ticket";
import { toast } from "sonner";
import { useEffect } from "react";

interface CreateTicketForm {
  title: string;
  description: string;
  priority: TicketPriority;
  type: TicketType;
  assignedTo?: string;
  dueDate?: string;
  relatedOrderId?: string;
  relatedProductId?: string;
}

const CreateTicket = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { storeType, storeId } = useParams<{ storeType: string; storeId: string }>();
  const { adminAuth, userId } = useAppSelector((state) => state.auth);

  const { formState } = useAppSelector((state) => state.tickets);
  const [tags, setTags] = useState<string[]>([]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<CreateTicketForm>({
    defaultValues: {
      priority: "normal",
      type: "general",
    },
  });

  const watchedPriority = watch("priority");
  const watchedType = watch("type");

  useEffect(() => {
    if (formState.success) {
      toast.success("Ticket created successfully");
      dispatch(resetFormState());
      navigate(`/${storeType}/${storeId}/tickets`);
    }
  }, [formState.success, dispatch, navigate, storeType, storeId]);

  useEffect(() => {
    if (formState.error) {
      toast.error(formState.error.message || "Failed to create ticket");
    }
  }, [formState.error]);

  const onSubmit: SubmitHandler<CreateTicketForm> = (data) => {
    const ticketData: CreateTicketDto = {
      title: data.title,
      subject: data.title,
      description: data.description,
      priority: data.priority,
      type: data.type,
      createdById: adminAuth?.userId || userId || storeId || "",
      createdByType: "seller",
      participants: [
        {
          userId: adminAuth?.userId || userId || storeId || "",
          userType: "seller",
          role: "requester",
          visible: true,
        },
      ],
      assignedTo: data.assignedTo,
      tags,
      dueDate: data.dueDate,
      relatedOrderId: data.relatedOrderId,
      relatedProductId: data.relatedProductId,
    };

    dispatch(createTicketRequest(ticketData));
  };

  const breadcrumbItems = [
    {
      label: "Tickets",
      href: `/${storeType}/${storeId}/tickets`,
    },
    {
      label: "Create Ticket",
      isActive: true,
    },
  ];

  const priorityOptions = [
    { label: "Low", value: "low" },
    { label: "Medium", value: "normal" },
    { label: "High", value: "high" },
    { label: "Urgent", value: "urgent" },
  ];

  const typeOptions = [
    { label: "Technical", value: "technical" },
    { label: "General", value: "general" },
    { label: "Website", value: "website" },
    { label: "Products", value: "products" },
    { label: "Orders", value: "orders" },
    { label: "Payments", value: "payments" },
    { label: "Shipping", value: "shipping" },
    { label: "Billing", value: "billing" },
    { label: "Account", value: "account" },
    { label: "Other", value: "other" },
  ];

  const getPriorityColor = (priority: TicketPriority) => {
    switch (priority) {
      case "low":
        return "bg-green-100 text-green-800 border-green-200";
      case "normal":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "high":
        return "bg-orange-100 text-orange-800 border-orange-200";
      case "urgent":
        return "bg-red-100 text-red-800 border-red-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  return (
    <div className="p-6">
      {/* Breadcrumb */}
      <div className="mb-6">
        <Breadcrumb items={breadcrumbItems} showBackButton={false} />
      </div>

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <TicketIcon className="h-6 w-6" />
          Create New Ticket
        </h1>
        <p className="text-muted-foreground">
          Create a new support ticket for tracking issues and communications
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="w-full">
        <div className="space-y-6">
          {/* Basic Information */}
          <FormFieldWrapper title="Basic Information">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="lg:col-span-2">
                <StandardFormInput
                  label="Ticket Title"
                  type="text"
                  placeholder="Brief description of the issue"
                  required
                  {...register("title", { required: "Title is required" })}
                  error={errors.title?.message}
                />
              </div>

              <StandardFormSelect
                label="Priority"
                value={watchedPriority}
                onValueChange={(value) =>
                  setValue("priority", value as TicketPriority)
                }
                options={priorityOptions}
                placeholder="Select priority"
              >
                <div className="p-2">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm font-medium">Current:</span>
                    <Badge
                      variant="outline"
                      className={`text-xs border ${getPriorityColor(watchedPriority)}`}
                    >
                      {watchedPriority}
                    </Badge>
                  </div>
                </div>
              </StandardFormSelect>

              <StandardFormSelect
                label="Ticket Type"
                value={watchedType}
                onValueChange={(value) => setValue("type", value as TicketType)}
                options={typeOptions}
                placeholder="Select ticket type"
              />
            </div>

            <div className="space-y-1">
              <label className="block text-[13px] font-normal text-muted-foreground">
                Description
              </label>
              <div className="bg-background border border-border rounded-xl overflow-hidden">
                <textarea
                  placeholder="Detailed description of the issue or request"
                  className="w-full px-4 py-2 bg-transparent placeholder-gray-500 outline-none min-h-[120px] resize-vertical"
                  {...register("description", {
                    required: "Description is required",
                  })}
                />
              </div>
              {errors.description && (
                <p className="text-red-500 text-xs">
                  {errors.description.message}
                </p>
              )}
            </div>

            <StandardFormTagInput
              label="Tags"
              tags={tags}
              onTagsChange={setTags}
              placeholder="Add relevant tags"
            />
          </FormFieldWrapper>

          {/* Assignment & Dates */}
          <FormFieldWrapper title="Assignment & Timeline">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <StandardFormInput
                label="Assigned To (Email)"
                type="email"
                placeholder="agent@example.com"
                {...register("assignedTo")}
              />

              <StandardFormInput
                label="Due Date"
                type="date"
                {...register("dueDate")}
              />
            </div>
          </FormFieldWrapper>

          {/* Related Items */}
          <FormFieldWrapper title="Related Items (Optional)">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <StandardFormInput
                label="Related Order ID"
                type="text"
                placeholder="Order ID if this ticket is related to an order"
                {...register("relatedOrderId")}
              />

              <StandardFormInput
                label="Related Product ID"
                type="text"
                placeholder="Product ID if this ticket is related to a product"
                {...register("relatedProductId")}
              />
            </div>
          </FormFieldWrapper>

          {/* Actions */}
          <div className="flex items-center justify-end gap-4 pt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(-1)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={formState.loading}
              className="flex items-center gap-2"
            >
              <Send className="h-4 w-4" />
              {formState.loading ? "Creating..." : "Create Ticket"}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default CreateTicket;
